import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import backgroundImage from '../images/10246984.jpeg';
import { Box, Palette, Image, HardDriveIcon } from 'lucide-react';

const HOSTGATOR_DOMAIN = 'https://nuedesigns.com';

const image1 = `${HOSTGATOR_DOMAIN}/assets/images/Bright.jpg`;
const image2 = `${HOSTGATOR_DOMAIN}/assets/images/Modern.jpg`;
const video1 = `${HOSTGATOR_DOMAIN}/assets/videos/Timeline 1.mp4`;
const video2 = `${HOSTGATOR_DOMAIN}/assets/videos/1BedApartment.mp4`;

// Add lightweight JPG posters next to your MP4s on HostGator
const video1Poster = `${HOSTGATOR_DOMAIN}/assets/videos/timeline-1-poster.jpg`;
const video2Poster = `${HOSTGATOR_DOMAIN}/assets/videos/1BedApartment-poster.jpg`;

function MyComponent() {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/contact');
  };

  return <button onClick={handleClick}>Go to Contact</button>;
}

const usePreventSave = () => {
  useEffect(() => {
    const preventSave = (e) => {
      if (e.ctrlKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
      }
    };

    const preventRightClick = (e) => {
      e.preventDefault();
    };

    document.addEventListener('keydown', preventSave);
    document.addEventListener('contextmenu', preventRightClick);

    return () => {
      document.removeEventListener('keydown', preventSave);
      document.removeEventListener('contextmenu', preventRightClick);
    };
  }, []);
};

const ServicesContainer = styled.div`
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: scroll; /* FIX: was fixed */
  color: rgb(15, 0, 0);
  min-height: 100vh;
`;

const Section = styled.section`
  font-family: "Helvetica";
  padding: 6rem 0;

  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 1900px) {
    max-width: 1600px;
  }
`;

const Title = styled.h1`
  color: #0a192f;
  font-family: "Raleway-Regular";
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  margin-top: -1rem;
  margin-bottom: 3rem;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 1rem;
  }

  @media (min-width: 1920px) {
    margin-top: 0rem;
  }

  @media (min-width: 2560px) {
    margin-top: 1rem;
  }
`;

const Subtitle = styled.p`
  color: #0a192f;
  font-family: "Helvetica";
  font-size: clamp(0.9rem, 1.5vw, 1.1rem);
  margin-bottom: 4rem;
  text-align: center;
`;

const Button = styled.button`
  background-color: #cec9ab;
  color: #0a192f;
  padding: 0.75rem 1.5rem;
  border-radius: 0.25rem;
  text-decoration: none;
  font-family: "Raleway-Regular";
  font-weight: bold;
  display: inline-block;
  transition: background-color 0.3s;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: #0a192f;
    color: #cec9ab;
    border-radius: 0.25rem;
  }
`;

const ServiceBoxContainer = styled.div`
  margin-top: -0.5rem;
  background-color: rgba(44, 31, 31, 0.65);
  padding: 2rem 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const ServiceGridWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 1900px) {
    max-width: 1600px;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 0.5rem;
  padding-top: 1rem;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ServiceContent = styled.div`
  padding: 1.5rem;
`;

const ServiceTitle = styled.h3`
  color: #0a192f;
  font-family: "Raleway-Regular";
  font-size: clamp(1.25rem, 2vw, 1.5rem);
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #0a192f;
  font-family: "Helvetica";
  height: 100px;
  overflow: hidden;
  transition: height 0.3s ease;
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  line-height: 1.75;
  max-width: 600px;
  margin: 0 auto;
  margin-top: 2rem;

  ${ServiceCard}:hover & {
    height: auto;
  }
`;

const ServiceIcon = styled.div`
  color: #0a192f;
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  margin-bottom: 1rem;
`;

const ServiceMedia = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;

  @media (min-width: 1900px) {
    height: 300px;
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.src});
  background-size: 95%;
  background-position: center;
  background-repeat: no-repeat;
`;

const ServiceVideo = styled.video`
  width: 98%;
  height: 100%;
  object-fit: cover;
`;

// Lazy-loaded video using IntersectionObserver
const LazyVideo = ({ src, poster }) => {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <ServiceMedia ref={containerRef}>
      {shouldLoad ? (
        <ServiceVideo
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={poster}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </ServiceVideo>
      ) : (
        <ServiceImage src={poster} />
      )}
    </ServiceMedia>
  );
};

const services = [
  {
    title: "Virtual Reality Property Tours",
    description:
      "Experience Properties in immersive VR with our integration. Walk through spaces, understand scale, and visualize potential like never before",
    icon: Box,
    media: { type: 'image', src: image1 },
  },
  {
    title: "Interior Design Customization",
    description:
      "Change wall colors, swap furniture, and experiment with different materials in real-time. Make confident design decisions with our intuitive VR interface",
    icon: Palette,
    media: { type: 'image', src: image2 },
  },
  {
    title: "Ray-Traced Renderings",
    description:
      "Generate stunning, photorealistic visualizations of your customized spaces with advanced ray-tracing technology for perfect lighting and reflections",
    icon: Image,
    media: { type: 'video', src: video2, poster: video2Poster },
  },
  {
    title: "Hardware Solutions",
    description:
      "Get started quickly with our Meta Quest 3 rental program. We provide all the hardware you need to deliver immersive VR experiences to your clients",
    icon: HardDriveIcon,
    media: { type: 'video', src: video1, poster: video1Poster },
  },
];

const ServiceMediaComponent = ({ media }) => {
  if (media.type === 'video') {
    return <LazyVideo src={media.src} poster={media.poster} />;
  }

  return (
    <ServiceMedia>
      <ServiceImage src={media.src} />
    </ServiceMedia>
  );
};

const Services = () => {
  usePreventSave();

  const navigate = useNavigate();

  const handleConsultationClick = () => {
    const message =
      "I'm interested in scheduling a free consultation for your VR solutions.";
    navigate('/contact', { state: { prefilledMessage: message } });
  };

  return (
    <ServicesContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Title>Our Services</Title>
            <Subtitle>
              Transform your Real Estate and Interior design workflow with our
              cutting-edge VR solutions
            </Subtitle>
            <div style={{ textAlign: 'center' }}>
              <Button onClick={handleConsultationClick}>
                Schedule a Free Consultation
              </Button>
            </div>
          </motion.div>
        </Container>
      </Section>

      <ServiceBoxContainer>
        <ServiceGridWrapper>
          <ServiceGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <ServiceMediaComponent media={service.media} />
                <ServiceContent>
                  <ServiceIcon>
                    <service.icon size={32} />
                  </ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>
                    {service.description}
                  </ServiceDescription>
                </ServiceContent>
              </ServiceCard>
            ))}
          </ServiceGrid>
        </ServiceGridWrapper>
      </ServiceBoxContainer>

      <Section>
        <Container>
          <div style={{ textAlign: 'center' }}>
            <Title>Try Our Interactive Demo</Title>
            <Subtitle>
              Experience a simplified version of our VR platform directly in
              your browser
            </Subtitle>
            <Button as="button" onClick={() => console.log('Launch demo')}>
              Coming Soon
            </Button>
          </div>
        </Container>
      </Section>
    </ServicesContainer>
  );
};

export default Services;
